import React from 'react';
import {InstructorDetails} from '@udemy-v2/browse-course';
import styles from './instructor-stats.module.scss';
import {Image} from '@udemy-v2/react-core-components';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';

import {BlockList, BlockListItem} from '@udemy-v2/react-core-components';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {CommunityPoweredBadge} from '../../instructor-community/community-powered-badge';

export interface InstructorStatsProps {
    instructor: InstructorDetails;
    showCommunityPoweredBadge?: boolean;
}

/**
 * InstructorStats component displays statistical information about a course instructor.
 *
 * @component
 * @example
 * ```jsx
 * <InstructorStats instructor={instructor} />
 * ```
 *
 * @param {InstructorStatsProps} props - The properties for the component.
 * @param {InstructorDetails} props.instructor - The instructor object containing statistical data.
 *
 * @returns {JSX.Element} A React component that renders the instructor's statistics including
 * total courses, total students, and average rating.
 */

export const InstructorStats: React.FC<InstructorStatsProps> = ({
    instructor,
    showCommunityPoweredBadge = false,
}) => {
    const {ninterpolate, interpolate, gettext} = useI18n();
    const {formatNumber} = useFormatNumber();
    const course = useClpCourse();
    const isGwgCourse = !!course?.contentCollectionInfo?.isInGooglePartnerCollection;

    const starRatingText = formatNumber((instructor.stats?.rating ?? 0).toFixed(1));
    const numStudentText =
        instructor.stats?.enrollmentCount === 0 || instructor.stats?.enrollmentCount === undefined
            ? 0
            : formatNumber(instructor.stats.enrollmentCount);
    const numCourseText =
        instructor.stats?.courseCount === 0 || instructor.stats?.courseCount === undefined
            ? 0
            : formatNumber(instructor.stats.courseCount);

    const numReviewText =
        instructor.stats?.reviewCount === 0 || instructor.stats?.reviewCount === undefined
            ? 0
            : formatNumber(instructor.stats.reviewCount);

    const renderInstructorPhoto = () => {
        if (showCommunityPoweredBadge && instructor.images.large) {
            return (
                <CommunityPoweredBadge
                    imageUrl={instructor.images.large}
                    displayName={instructor.name}
                    size="lg"
                    className={styles['instructor-stats-badge']}
                />
            );
        }

        return (
            <Image
                src={instructor.images.large}
                alt={instructor.name}
                className={styles['instructor-stats-image']}
            />
        );
    };

    return (
        <div className={styles['instructor-stats']}>
            <div className={styles['instructor-stats-row']}>
                {renderInstructorPhoto()}
                <BlockList size="small" padding="tight">
                    <BlockListItem
                        data-purpose="stat"
                        key="instructor-stats-rating"
                        icon={<Icon label={false} glyph="rating-star" />}
                    >
                        {interpolate(
                            gettext('%(rating)s Instructor Rating'),
                            {
                                rating: starRatingText,
                            },
                            true,
                        )}
                    </BlockListItem>
                    <BlockListItem
                        data-purpose="stat"
                        key="instructor-stats-reviews"
                        icon={<Icon label={false} glyph="certificate" />}
                    >
                        {ninterpolate(
                            gettext('%(count)s Review'),
                            gettext('%(count)s Reviews'),
                            instructor.stats?.reviewCount ?? 0,
                            {count: numReviewText},
                        )}
                    </BlockListItem>
                    {!isGwgCourse && (
                        <BlockListItem
                            data-purpose="stat"
                            key="instructor-stats-students"
                            icon={<Icon label={false} glyph="people" />}
                        >
                            {ninterpolate(
                                gettext('%(count)s Student'),
                                gettext('%(count)s Students'),
                                instructor.stats?.enrollmentCount ?? 0,
                                {count: numStudentText},
                            )}
                        </BlockListItem>
                    )}
                    <BlockListItem
                        data-purpose="stat"
                        key="instructor-stats-courses"
                        icon={<Icon label={false} glyph="play" />}
                    >
                        {ninterpolate(
                            gettext('%(count)s Course'),
                            gettext('%(count)s Courses'),
                            instructor.stats?.courseCount ?? 0,
                            {count: numCourseText},
                        )}
                    </BlockListItem>
                </BlockList>
            </div>
        </div>
    );
};
